/* eslint-disable @typescript-eslint/no-explicit-any */

import pino from 'pino';

// Helper function to generate/get session ID
const getSessionId = (): string => {
  if (globalThis.window === undefined) return 'ssr';

  let sessionId = sessionStorage.getItem('log-session-id');
  if (sessionId) {
    return sessionId;
  }
  sessionId = `client-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem('log-session-id', sessionId);
  return sessionId;
};

// Base fields pino's browser build never merges into console output or
// `transmit` events on its own (unlike the Node build) — re-attach manually
// wherever a record is built from a log event.
const getBaseFields = () => ({
  service: 'lamesarv.com',
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.NODE_ENV || 'production',
  source: 'browser',
  sessionId: globalThis.window === undefined ? 'ssr' : getSessionId(),
});

// Buffers pino `transmit` events and ships them to the server logs endpoint in
// batches. `browser.transmit` is pino's hook for this (unlike `logger.write`,
// which the browser build always sets to a noop and never calls internally).
const createTransmit = (): { level: pino.Level; send: (level: pino.Level, logEvent: pino.LogEvent) => void } => {
  const logBuffer: Record<string, unknown>[] = [];
  const BATCH_SIZE = 10;
  const BATCH_INTERVAL = 5000; // 5 seconds
  let timer: ReturnType<typeof setTimeout> | null = null;

  const sendLogs = async () => {
    if (logBuffer.length === 0) return;

    const logs = [...logBuffer];
    logBuffer.length = 0;

    try {
      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          logs: logs.map((log) => ({
            ...log,
            clientTimestamp: log.time || Date.now(),
          })),
          metadata: {
            userAgent: navigator.userAgent,
            url: globalThis.location.href,
            timestamp: new Date().toISOString(),
            sessionId: getSessionId(),
          },
        }),
      });
    } catch (error) {
      // Silently fail to avoid infinite loop if we're in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to send client logs to server:', error);
      }
    }
  };

  return {
    level: 'info',
    send: (level, logEvent) => {
      const [first, ...rest] = logEvent.messages;
      const hasMergingObject = typeof first === 'object' && first !== null;
      const mergingObject = hasMergingObject ? (first as Record<string, unknown>) : undefined;
      const msg = hasMergingObject ? rest[0] : first;

      logBuffer.push({
        ...getBaseFields(),
        ...Object.assign({}, ...logEvent.bindings),
        ...mergingObject,
        time: logEvent.ts,
        level,
        msg,
      });

      if (logBuffer.length >= BATCH_SIZE) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        void sendLogs();
      } else if (!timer) {
        timer = setTimeout(() => {
          timer = null;
          void sendLogs();
        }, BATCH_INTERVAL);
      }
    },
  };
};

// Client-side logger configuration
const baseConfig: pino.LoggerOptions = {
  level: process.env.NEXT_PUBLIC_LOG_LEVEL || 'info',
  browser: {
    // Use console methods for browser logging
    write: {
      debug: (o: any) => console.debug(o),
      info: (o: any) => console.info(o),
      warn: (o: any) => console.warn(o),
      error: (o: any) => console.error(o),
      fatal: (o: any) => console.error(o),
    },
    // Format log objects for better browser console output
    serialize: true,
    // Ship every log (info and above) to /api/logs; undefined during SSR
    // since there's no window to fetch from.
    transmit: globalThis.window === undefined ? undefined : createTransmit(),
  },
  base: getBaseFields(),
};

// Create singleton logger instance
let clientLogger: pino.Logger | null = null;

export const getClientLogger = (name?: string): pino.Logger => {
  if (!clientLogger) {
    clientLogger = pino(baseConfig);
  }

  return name ? clientLogger.child({ module: name }) : clientLogger;
};

// React hook for component logging
export const useLogger = (componentName: string) => {
  const logger = getClientLogger(`component.${componentName}`);

  return {
    logger,
    logEvent: (eventName: string, data?: any) => {
      logger.info({ event: eventName, ...data }, `Event: ${eventName}`);
    },
    logError: (error: Error, context?: any) => {
      logger.error(
        {
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name,
          },
          ...context,
        },
        error.message,
      );
    },
    logPerformance: (metric: string, value: number, unit = 'ms') => {
      logger.info(
        {
          performance: {
            metric,
            value,
            unit,
          },
        },
        `Performance: ${metric} = ${value}${unit}`,
      );
    },
  };
};

// Browser performance monitoring
export const initPerformanceLogging = () => {
  const logger = getClientLogger('performance');

  // Log page load metrics
  if (globalThis.window !== undefined && globalThis.performance) {
    globalThis.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navigation) {
        logger.info(
          {
            performance: {
              dns: navigation.domainLookupEnd - navigation.domainLookupStart,
              tcp: navigation.connectEnd - navigation.connectStart,
              request: navigation.responseStart - navigation.requestStart,
              response: navigation.responseEnd - navigation.responseStart,
              domInteractive: navigation.domInteractive - navigation.fetchStart,
              domComplete: navigation.domComplete - navigation.fetchStart,
              loadComplete: navigation.loadEventEnd - navigation.fetchStart,
            },
          },
          'Page load performance metrics',
        );
      }
    });

    // Log Core Web Vitals
    if ('PerformanceObserver' in globalThis) {
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries.at(-1) as any;
        logger.info(
          {
            webVitals: {
              metric: 'LCP',
              value: lastEntry?.renderTime || lastEntry?.loadTime || 0,
            },
          },
          'Web Vitals: LCP',
        );
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Interaction to Next Paint (INP)
      let inpValue = 0;
      const inpObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (!entry.interactionId || entry.duration <= inpValue) return;

          inpValue = entry.duration;
          logger.info(
            {
              webVitals: {
                metric: 'INP',
                value: inpValue,
              },
            },
            'Web Vitals: INP',
          );
        });
      });
      inpObserver.observe({ type: 'event', buffered: true, durationThreshold: 40 } as any);

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            logger.info(
              {
                webVitals: {
                  metric: 'CLS',
                  value: clsValue,
                },
              },
              'Web Vitals: CLS',
            );
          }
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    }
  }
};

// Error boundary logging
export const logErrorBoundary = (error: Error, errorInfo: any) => {
  // Also report to RUM Error Tracking (ignored when RUM isn't initialized). The SDK is imported
  // on demand, in the browser only: a static import would evaluate it in every server bundle
  // that pulls in this module and trigger "SDK is loaded more than once".
  if (globalThis.window !== undefined) {
    import('@datadog/browser-rum').then(({ datadogRum }) => datadogRum.addError(error, errorInfo));
  }

  const logger = getClientLogger('error-boundary');
  logger.error(
    {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      errorInfo,
    },
    'React Error Boundary triggered',
  );
};

export type ClientLogger = ReturnType<typeof getClientLogger>;
